export interface LogRecord {
    logId: string
    levelLabel: string
    timestamp: number
    template: string
    args?: Record<string, unknown>
    context?: Record<string, string>
}

export interface BatchedLogTransport {
    enqueue: (record: Omit<LogRecord, 'logId'>) => void
    flush: () => Promise<void>
    queueSize: () => number
}

interface CreateTransportOptions {
    logProcessorBaseUrl?: string
    avenProject: string
    getContext?: () => Record<string, string>
    clientSideLogsEnabled: boolean
    maxBatchSize?: number
    flushIntervalMs?: number
    maxQueueSize?: number

    scheduleDelay?: (ms: number) => Promise<void>
}

const DEFAULT_MAX_BATCH_SIZE = 10
const DEFAULT_FLUSH_INTERVAL_MS = 2000
const DEFAULT_MAX_QUEUE_SIZE = 200

const MAX_RECORD_BYTES = 6_000
const MAX_MESSAGE_BYTES = 1_500

const MAX_SEND_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 200

export function createBatchedLogTransport(opts: CreateTransportOptions): BatchedLogTransport {
    const url = opts.logProcessorBaseUrl ? opts.logProcessorBaseUrl.replace(/\/+$/, '') + '/' : null
    const maxBatchSize = opts.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE
    const flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS
    const maxQueueSize = opts.maxQueueSize ?? DEFAULT_MAX_QUEUE_SIZE
    const scheduleDelay = opts.scheduleDelay ?? defaultScheduleDelay

    const queue: LogRecord[] = []
    const flusher = createFlusher(flushIntervalMs, () => void flush())
    let inFlight: Promise<void> | null = null
    let overflowDropped = 0

    // Surface transport drops as a WARN record so they reach even in prod, instead of being silently swallowed.
    const reportDrop = (template: string, args: Record<string, unknown>): void => {
        enqueue({ levelLabel: 'WARN', timestamp: Date.now(), template, args })
    }

    function enqueue(input: Omit<LogRecord, 'logId'>): void {
        if (!url) return
        const record: LogRecord = {
            ...input,
            logId: generateId(),
            context: { source: opts.avenProject, ...opts.getContext?.() },
        }
        while (queue.length >= maxQueueSize) {
            queue.shift()
            overflowDropped += 1
        }
        queue.push(record)

        const isUrgent = record.levelLabel === 'ERROR' || record.levelLabel === 'FATAL'
        if (queue.length >= maxBatchSize || isUrgent) {
            flusher.cancel()
            void flush()
        } else {
            flusher.notify()
        }
    }

    async function flush(): Promise<void> {
        if (inFlight) {
            await inFlight
            if (queue.length === 0) return
        }
        if (!url || queue.length === 0) return

        const batch = queue.splice(0, queue.length)
        let body: string
        try {
            body = encodeBatch(batch)
        } catch (error) {
            if (opts.clientSideLogsEnabled) {
                // eslint-disable-next-line no-console -- intentional: surface logging failures in dev
                console.error('logger encode failure; dropping batch', error)
            }
            reportDrop('[client-logs] encode failure; dropped records', { droppedRecords: batch.length })
            return
        }

        inFlight = sendWithRetry(url, body, opts.clientSideLogsEnabled, scheduleDelay)
            .then((delivered) => {
                if (!delivered) {
                    // eslint-disable-next-line no-console -- intentional: dev-mode visibility into transport-failure path
                    if (opts.clientSideLogsEnabled) console.warn(`logger: ${batch.length} records dropped after ${MAX_SEND_ATTEMPTS} attempts`)
                    reportDrop(`[client-logs] dropped records after ${MAX_SEND_ATTEMPTS} send attempts`, { droppedRecords: batch.length })
                }
            })
            .finally(() => {
                inFlight = null
                if (overflowDropped > 0) {
                    const dropped = overflowDropped
                    overflowDropped = 0 // reset before reporting so the report's own overflow isn't wiped
                    reportDrop('[client-logs] dropped records: queue overflow', { droppedRecords: dropped })
                }
            })
        await inFlight
    }

    if (typeof window !== 'undefined' && url) {
        const handleHide = () => flushSync(url, queue.splice(0, queue.length), opts.clientSideLogsEnabled)
        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') handleHide()
        })
        window.addEventListener('pagehide', handleHide)
    }

    return {
        enqueue,
        flush,
        queueSize: () => queue.length,
    }
}

function createFlusher(intervalMs: number, onFlush: () => void) {
    let timer: ReturnType<typeof setTimeout> | null = null
    return {
        notify(): void {
            if (timer !== null) return
            timer = setTimeout(() => {
                timer = null
                onFlush()
            }, intervalMs)
        },
        cancel(): void {
            if (timer === null) return
            clearTimeout(timer)
            timer = null
        },
    }
}

function encodeBatch(batch: LogRecord[]): string {
    return `[${batch.map(serializeRecord).join(',')}]`
}

function serializeRecord(record: LogRecord): string {
    const payload: LogRecord = {
        logId: record.logId,
        levelLabel: record.levelLabel,
        timestamp: record.timestamp,
        template: truncate(record.template, MAX_MESSAGE_BYTES),
        args: record.args,
    }
    if (record.context !== undefined) payload.context = record.context

    const encoded = JSON.stringify(payload)
    if (encoded.length <= MAX_RECORD_BYTES) return encoded

    // Over the cap: trim oversized string args (keeping template/level/context) and mark the trim.
    if (record.args !== undefined) {
        const trimmed = trimArgsToFit(payload, MAX_RECORD_BYTES, encoded.length)
        const trimmedEncoded = JSON.stringify({ ...payload, args: trimmed })
        if (trimmedEncoded.length <= MAX_RECORD_BYTES) return trimmedEncoded
    }

    // Last resort: retain identity + an observable sentinel.
    const source = payload.args?.source
    return JSON.stringify({
        logId: record.logId,
        levelLabel: record.levelLabel,
        template: `[record exceeded ${MAX_RECORD_BYTES} bytes; replaced with sentinel]`,
        args: { source, _truncatedBytes: encoded.length },
        timestamp: record.timestamp,
    })
}

// A trimmed arg keeps at least this many bytes so a record with many keys still yields a
// readable snippet rather than a useless stub.
const MIN_TRUNCATED_ARG_BYTES = 10
// Per-key JSON reserve: quotes/colon/comma framing plus escape-sequence inflation (a stack
// with control chars encodes larger than its raw length). Sized generously — serializeRecord
// re-checks `<= cap` and falls back to the sentinel if this ever underestimates.
const PER_ARG_FRAMING_BYTES = 64

function trimArgsToFit(payload: LogRecord, cap: number, originalLength: number): Record<string, unknown> {
    const args = payload.args
    const keys = Object.keys(args ?? {})
    if (keys.length === 0) return args ?? {}

    // Byte budget the args may occupy: the record cap minus the exact payload size (everything
    // except args), minus per-key JSON framing, minus the _truncatedBytes marker's own digits.
    const { args: _args, ...shell } = payload
    const shellBytes = JSON.stringify(shell).length
    const markerBytes = String(originalLength).length
    const argsBudget = cap - shellBytes - keys.length * PER_ARG_FRAMING_BYTES - markerBytes

    const perKeyBytes = Math.max(MIN_TRUNCATED_ARG_BYTES, Math.floor(argsBudget / keys.length))

    const trimmed: Record<string, unknown> = {}
    for (const key of keys) {
        const value = args?.[key]
        trimmed[key] = typeof value === 'string' ? truncate(value, perKeyBytes) : value
    }
    trimmed._truncatedBytes = originalLength
    return trimmed
}

const TRUNCATION_ELLIPSIS = '...'
// Bytes reserved for the `...[truncated N chars]` marker appended to a truncated value.
// Small budgets fall back to a plain ellipsis (or a raw slice) rather than a negative
// slice from the end, which would expand the payload instead of shrinking it.
const TRUNCATION_MARKER_RESERVE = 30

function truncate(value: string, maxBytes: number): string {
    if (value.length <= maxBytes) return value
    if (maxBytes < TRUNCATION_ELLIPSIS.length) return value.slice(0, maxBytes)
    if (maxBytes <= TRUNCATION_MARKER_RESERVE) {
        return `${value.slice(0, maxBytes - TRUNCATION_ELLIPSIS.length)}${TRUNCATION_ELLIPSIS}`
    }
    const sliceLen = maxBytes - TRUNCATION_MARKER_RESERVE
    const truncatedCount = value.length - sliceLen
    return `${value.slice(0, sliceLen)}${TRUNCATION_ELLIPSIS}[truncated ${truncatedCount} chars]`
}

function buildPostInit(body: string): RequestInit {
    return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: '*/*' },
        body,
        keepalive: true,
    }
}

async function sendWithRetry(url: string, body: string, clientSideLogsEnabled: boolean, scheduleDelay: (ms: number) => Promise<void>): Promise<boolean> {
    for (let attempt = 0; attempt < MAX_SEND_ATTEMPTS; attempt += 1) {
        try {
            const response = await fetch(url, buildPostInit(body))
            if (response.ok) return true
            if (response.status >= 400 && response.status < 500) return false
        } catch (error) {
            if (clientSideLogsEnabled) {
                // eslint-disable-next-line no-console -- intentional: surface logger transport failures only in dev
                console.error(`logger transport attempt ${attempt + 1} failed`, error)
            }
        }
        if (attempt < MAX_SEND_ATTEMPTS - 1) {
            await scheduleDelay(RETRY_BASE_DELAY_MS * 2 ** attempt)
        }
    }
    return false
}

function flushSync(url: string, batch: LogRecord[], clientSideLogsEnabled: boolean): void {
    if (batch.length === 0) return
    try {
        const body = encodeBatch(batch)
        const blob = new Blob([body], { type: 'application/json' })
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url, blob)) {
            return
        }
        void fetch(url, buildPostInit(body))
    } catch (error) {
        if (clientSideLogsEnabled) {
            // eslint-disable-next-line no-console -- intentional: surface logger transport failures only in dev
            console.error('logger pagehide transport failure', error)
        }
    }
}

function defaultScheduleDelay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
        setTimeout(resolve, ms)
    })
}

function generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
